import React, { Suspense, lazy, useEffect } from "react";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuthStore, useLangStore } from "@/store/useStore";
import i18n from "@/i18n";
import AppLayout from "@/layouts/AppLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Settings from "@/pages/Settings";

// Lazy-load heavier pages to speed up initial mobile load
const CreateGroup = lazy(() => import("@/pages/CreateGroup"));
const GroupDetail = lazy(() => import("@/pages/GroupDetail"));
const AddMember = lazy(() => import("@/pages/AddMember"));
const Defaulters = lazy(() => import("@/pages/Defaulters"));
const Reports = lazy(() => import("@/pages/Reports"));
const MemberView = lazy(() => import("@/pages/MemberView"));
const AdminUsers = lazy(() => import("@/pages/AdminUsers"));
const Signup = lazy(() => import("@/pages/Signup"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));

function Protected({ children }) {
  const { token } = useAuthStore();
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function RootRedirect() {
  const { token } = useAuthStore();
  if (token) return <Navigate to="/dashboard" replace />;
  return <Navigate to="/login" replace />;
}

const PageLoader = () => (
  <div className="flex justify-center py-16" data-testid="page-loader">
    <Loader2 className="h-6 w-6 animate-spin text-[#1A3C6E]" />
  </div>
);

function App() {
  const { lang } = useLangStore();

  useEffect(() => {
    if (i18n.language !== lang) i18n.changeLanguage(lang);
  }, [lang]);

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route
            path="/admin/login"
            element={<Suspense fallback={<PageLoader />}><AdminLogin /></Suspense>}
          />
          <Route
            path="/signup"
            element={<Suspense fallback={<PageLoader />}><Signup /></Suspense>}
          />
          <Route
            path="/m/:token"
            element={<Suspense fallback={<PageLoader />}><MemberView /></Suspense>}
          />

          <Route
            element={
              <Protected>
                <AppLayout />
              </Protected>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route
              path="/groups/new"
              element={<Suspense fallback={<PageLoader />}><CreateGroup /></Suspense>}
            />
            <Route
              path="/groups/:id"
              element={<Suspense fallback={<PageLoader />}><GroupDetail /></Suspense>}
            />
            <Route
              path="/groups/:id/members/new"
              element={<Suspense fallback={<PageLoader />}><AddMember /></Suspense>}
            />
            <Route
              path="/defaulters"
              element={<Suspense fallback={<PageLoader />}><Defaulters /></Suspense>}
            />
            <Route
              path="/reports"
              element={<Suspense fallback={<PageLoader />}><Reports /></Suspense>}
            />
            <Route path="/settings" element={<Settings />} />
            <Route
              path="/admin"
              element={<Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense>}
            />
            <Route
              path="/admin/users"
              element={<Suspense fallback={<PageLoader />}><AdminUsers /></Suspense>}
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-center" richColors closeButton />
      </BrowserRouter>
    </div>
  );
}

export default App;
